/**
 * The options table's figures (Drivers programme, OR-30.47): for each setting on offer, what it costs this year, what it adds to the driver (in the driver's own units) and what it is worth a share, all against the standing
 * policy, as a product leader would lay the options out. The figures are the engine's `previewNextStep` (ADR-0091): the draft's setting (`yearEndDrivers.draft`), the standing policy (`yearEndDrivers.noChange`) and the
 * settings one step either side of the draft (`nextStep`); a setting further away has none, and prints "–". Nothing is estimated here. Pure.
 */
import type { BusinessDrivers, PreviewResult } from "@bsim/domain";
import { formatUsdPerShare, formatUsdScaled } from "../../format";
import type { PreviewSteps } from "../usePreview";

export interface SettingFigures { readonly cost: string; readonly adds: string; readonly value: string }
const DASH = "–";
const NO_CHANGE = "No change";

const sum = (xs: readonly number[]): number => xs.reduce((t, x) => t + x, 0);
const total = (lines: BusinessDrivers["opexUsdByLine"]): number | undefined => (lines === undefined ? undefined : sum(Object.values(lines)));
const oneOffOf = (lines: BusinessDrivers["opexUsdByLine"]): number | undefined => (lines === undefined ? undefined : sum(Object.entries(lines).filter(([k]) => k.startsWith("oneOff")).map(([, v]) => v)));
const near = (v: number, unit: number): boolean => Math.abs(v) < unit;
const signedCount = (v: number, one: string, many: string): string => (near(v, 0.05) ? NO_CHANGE : `${v > 0 ? "+" : "−"}${Math.abs(v).toFixed(1)} ${Math.abs(v) === 1 ? one : many}`);
const signedPct = (v: number, what: string): string => (near(v, 0.0005) ? NO_CHANGE : `${v > 0 ? "+" : "−"}${Math.abs(v * 100).toFixed(1)}% ${what}`);
const money = (v: number): string => (near(v, 5e4) ? NO_CHANGE : formatUsdScaled(v, { signed: true }));

type Read = (d: BusinessDrivers) => number | undefined;
const diff = (read: Read, d: BusinessDrivers, none: BusinessDrivers): number | null => { const a = read(d), b = read(none); return a === undefined || b === undefined ? null : a - b; };

/** The products a marketing decision buys (the keys of the drivers' by-product records), for the market share and the business it won. */
const MARKETING_PRODUCTS: Readonly<Record<string, readonly string[]>> = {
  "loans.consumer.growth": ["consumer_auto", "card"],
  "deposits.retailChecking.marketing": ["int_checking"], "deposits.retailMm.marketing": ["mmda_retail"], "deposits.cd.marketing": ["time_cd"], "deposits.commercialMm.marketing": ["mmda_indexed"],
};
const TEAM_OF: Readonly<Record<string, "ci" | "cre" | "resi">> = { "loans.ci.growth": "ci", "loans.cre.growth": "cre", "loans.resi.growth": "resi" };
const productSum = (rec: (d: BusinessDrivers) => BusinessDrivers["marketingFlowUsdByProduct"], ids: readonly string[]): Read => (d) => { const r = rec(d); return r === undefined ? undefined : sum(ids.map((i) => r[i] ?? 0)); };
const productMean = (ids: readonly string[]): Read => (d) => { const r = d.shareRateByProduct, xs = ids.flatMap((i) => (r?.[i] === undefined ? [] : [r[i] as number])); return xs.length === 0 ? undefined : sum(xs) / xs.length; };

interface Described { readonly cost: (d: BusinessDrivers, none: BusinessDrivers) => string; readonly adds: (d: BusinessDrivers, none: BusinessDrivers) => string }
const opexCost = (d: BusinessDrivers, none: BusinessDrivers): string => { const v = diff((x) => total(x.opexUsdByLine), d, none); return v === null ? DASH : money(v); };

/** How a decision's setting is read off the engine's drivers: its cost this year and what it adds to the driver it rests on. Absent = the decision has no driver the probe reads (its value still shows). */
function describe(decisionId: string): Described | undefined {
  const team = TEAM_OF[decisionId];
  if (team !== undefined) {
    const who = team === "resi" ? ["loan officer", "loan officers"] as const : ["banker", "bankers"] as const;
    return {
      cost: opexCost,
      adds: (d, none) => {
        const heads = diff((x) => x.teams?.[team].headcountCount, d, none), seats = diff((x) => x.teams?.[team].seatsAtWorkCount, d, none);
        if (heads === null) return DASH;
        return `${signedCount(heads, who[0], who[1])}${seats === null || near(heads, 0.05) ? "" : `, ${seats > 0 ? "+" : "\u2212"}${Math.abs(seats).toFixed(1)} at full work by the year's end`}`;
      },
    };
  }
  const products = MARKETING_PRODUCTS[decisionId];
  if (products !== undefined) {
    return {
      // The cost of a marketing setting is the year's marketing spend it changes, not the whole expense line (volume costs move other lines).
      cost: (d, none) => { const v = diff(productSum((x) => x.marketingUsdByProduct, products), d, none); return v === null ? DASH : money(v); },
      adds: (d, none) => {
        const bought = diff(productSum((x) => x.marketingFlowUsdByProduct, products), d, none), share = diff(productMean(products), d, none);
        if (bought === null && share === null) return DASH;
        const parts = [bought === null ? null : near(bought, 5e4) ? "no new business" : `${formatUsdScaled(bought, { signed: true })} new business`, share === null ? null : near(share, 5e-5) ? null : `${share > 0 ? "+" : "−"}${Math.abs(share * 100).toFixed(2)} pt of the market`];
        return parts.filter((x) => x !== null).join(", ") || NO_CHANGE;
      },
    };
  }
  if (decisionId === "operations.tech") {
    // The year's spend reaches the capability stock after the build lag (07.4), so in the first year the index barely moves: the cell says what has moved, and when nothing has, that the spend is still building.
    const stock: Read = (x) => (x.techStockUsdByTheme === undefined ? undefined : sum(Object.values(x.techStockUsdByTheme)));
    const spend: Read = (x) => (x.techSpendUsdByTheme === undefined ? undefined : sum(Object.values(x.techSpendUsdByTheme)));
    return {
      cost: opexCost,
      adds: (d, none) => {
        const built = diff(stock, d, none), index = diff((x) => x.capabilityIndex, d, none), spent = diff(spend, d, none);
        if (built === null) return DASH;
        if (!near(built, 5e4)) return `${formatUsdScaled(built, { signed: true })} of capability built${index === null || near(index, 0.0005) ? "" : `, ${signedPct(index, "capability")}`}`;
        return spent === null || near(spent, 5e4) ? NO_CHANGE : "None yet: the change is still building";
      },
    };
  }
  if (decisionId === "operations.branches") return { cost: opexCost, adds: (d, none) => { const v = diff((x) => x.branchCount, d, none); return v === null ? DASH : signedCount(v, "branch", "branches"); } };
  if (decisionId === "operations.peopleAndFees") return { cost: opexCost, adds: (d, none) => { const v = diff((x) => x.serviceIndex, d, none); return v === null ? DASH : signedPct(v, "service"); } };
  if (decisionId === "operations.expense") {
    // The cost-programme table's figures to the dollar (DE1): the one-off charge now, and the recurring saving of the year.
    return {
      cost: (d, none) => { const v = diff((x) => oneOffOf(x.opexUsdByLine), d, none); return v === null ? DASH : near(v, 5e4) ? "No one-off charge" : money(v); },
      adds: (d, none) => {
        const a = diff((x) => { const t = total(x.opexUsdByLine), o = oneOffOf(x.opexUsdByLine); return t === undefined || o === undefined ? undefined : t - o; }, d, none);
        return a === null ? DASH : near(a, 5e4) ? NO_CHANGE : `${a < 0 ? "Saves" : "Costs"} ${formatUsdScaled(Math.abs(a))} a year`;
      },
    };
  }
  return undefined;
}

export interface StepFigures {
  /** The figures of a setting by its label: the draft's own, the standing policy's, and the settings one step either side. */
  readonly byLabel: ReadonlyMap<string, SettingFigures>;
  /** The labels reached one step each way from the draft, in order up then down. */
  readonly stepped: readonly string[];
}

/**
 * The figures per setting. `ownValueUsdPerShare` is the draft's setting alone against the standing policy (the trace's rows for this decision; null while it is not there); a stepped setting's value is that plus what the step
 * adds to the whole draft's look-through value (the step is the draft with this one decision moved), so every row is read on the one basis: this decision, against the standing policy.
 */
export function stepFigures(decisionId: string, steps: PreviewSteps | null, pickLabel: string | undefined, standingLabel: string | undefined, ownValueUsdPerShare: number | null): StepFigures {
  const byLabel = new Map<string, SettingFigures>(), stepped: string[] = [];
  const both = [steps?.up, steps?.down].filter((r): r is PreviewResult => r !== undefined);
  const first = both[0];
  const set = first?.yearEndDrivers;
  const how = describe(decisionId);
  const draftValue = first?.lookThrough?.valueCreatedUsdPerShare;
  if (set !== undefined && pickLabel !== undefined) {
    byLabel.set(pickLabel, { cost: how?.cost(set.draft, set.noChange) ?? DASH, adds: how?.adds(set.draft, set.noChange) ?? DASH, value: ownValueUsdPerShare === null ? DASH : formatUsdPerShare(ownValueUsdPerShare, { signed: true }) });
  }
  if (standingLabel !== undefined) byLabel.set(standingLabel, { cost: set === undefined || how === undefined ? DASH : NO_CHANGE, adds: set === undefined || how === undefined ? DASH : NO_CHANGE, value: formatUsdPerShare(0) });
  for (const r of both) {
    const step = r.nextStep;
    if (step === undefined || set === undefined) continue;
    const value = ownValueUsdPerShare === null || draftValue === undefined ? null : ownValueUsdPerShare + (step.valueUsdPerShare - draftValue);
    byLabel.set(step.toLabel, { cost: how?.cost(step.businessDrivers, set.noChange) ?? DASH, adds: how?.adds(step.businessDrivers, set.noChange) ?? DASH, value: value === null ? DASH : formatUsdPerShare(value, { signed: true }) });
    stepped.push(step.toLabel);
  }
  return { byLabel, stepped };
}
